import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Search, Mail, MapPin, Loader2, Copy, Building2, Check, X, Key, Trash2, BellRing } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { api } from '../../lib/api';
import { toast } from 'sonner';

const OWN_BRANDS = ['Shell', 'UNO'];

const emptyForm = { name: '', display_alias: '', email: '', role: 'capturer', zone_id: '', own_brand: 'Shell', assigned_station_ids: [], password: '', price_email_enabled: true };

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [stations, setStations] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [createdUser, setCreatedUser] = useState(null);
  const [pushStatus, setPushStatus] = useState({});
  const [testingPush, setTestingPush] = useState(null);

  // Station picker filters (Capturador role)
  const [stationQ, setStationQ] = useState('');
  const [onlyOwn, setOnlyOwn] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.all([api.get('/users'), api.get('/stations'), api.get('/zones'), api.get('/users/push-status')])
      .then(([u, s, z, p]) => { setUsers(u.data); setStations(s.data); setZones(z.data); setPushStatus(p.data || {}); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = users.filter(u => !q || u.name.toLowerCase().includes(q.toLowerCase()) || u.email.toLowerCase().includes(q.toLowerCase()));

  const openCreate = () => { setEditing(null); setForm(emptyForm); setStationQ(''); setOnlyOwn(true); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u.id);
    setForm({
      name: u.name, display_alias: u.display_alias || '', email: u.email, role: u.role,
      zone_id: u.zone_id || '',
      own_brand: u.own_brand || 'Shell',
      assigned_station_ids: u.assigned_station_ids || [],
      password: '',
      price_email_enabled: u.price_email_enabled !== false,
    });
    setStationQ(''); setOnlyOwn(true);
    setOpen(true);
  };

  const stationOptions = useMemo(() => {
    const t = stationQ.trim().toLowerCase();
    return stations
      .filter(s => !onlyOwn || s.is_own === true)
      .filter(s => {
        if (!t) return true;
        return (
          (s.name || '').toLowerCase().includes(t) ||
          (s.brand || '').toLowerCase().includes(t) ||
          (s.bu || '').toLowerCase().includes(t) ||
          (s.address || '').toLowerCase().includes(t)
        );
      });
  }, [stations, stationQ, onlyOwn]);

  const submit = async () => {
    if (!form.name || !form.email) { toast.error('Nombre y correo requeridos'); return; }
    try {
      if (editing) {
        const patch = {
          name: form.name,
          display_alias: form.display_alias.trim(),
          email: form.email,
          role: form.role,
          zone_id: form.zone_id || null,
          own_brand: form.own_brand,
          assigned_station_ids: form.assigned_station_ids,
          price_email_enabled: form.price_email_enabled !== false,
        };
        if (form.password && form.password.length >= 4) patch.password = form.password;
        await api.patch(`/users/${editing}`, patch);
        toast.success(form.password ? 'Usuario actualizado · contraseña cambiada' : 'Usuario actualizado');
      } else {
        const payload = { ...form, password: form.password || undefined };
        const r = await api.post('/users', payload);
        setCreatedUser(r.data);
      }
      setOpen(false); setEditing(null); setForm(emptyForm); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`¿Eliminar definitivamente la cuenta de ${u.name} (${u.email})?\nEsta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success('Usuario eliminado');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al eliminar'); }
  };

  const toggleStatus = async (u) => {
    const next = u.status === 'active' ? 'inactive' : 'active';
    try { await api.patch(`/users/${u.id}`, { status: next }); load(); }
    catch (e) { toast.error('Error al actualizar'); }
  };

  const approveUser = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { status: 'active' });
      toast.success(`${u.name} aprobado`);
      load();
    } catch (e) { toast.error('Error al aprobar'); }
  };

  const rejectUser = async (u) => {
    if (!window.confirm(`¿Rechazar el acceso a ${u.name}? La cuenta quedará inactiva.`)) return;
    try {
      await api.patch(`/users/${u.id}`, { status: 'inactive' });
      toast.success('Solicitud rechazada');
      load();
    } catch (e) { toast.error('Error al rechazar'); }
  };

  const pendingUsers = users.filter(u => u.status === 'pending');

  const sendTestPush = async (u) => {
    setTestingPush(u.id);
    try {
      const r = await api.post(`/users/${u.id}/push-test`);
      const { subscriptions, sent, failed, results } = r.data;
      if (subscriptions === 0) toast.warning(`${u.name} no tiene notificaciones activadas en ningún dispositivo`);
      else if (failed === 0) toast.success(`Push de prueba enviado a ${sent} dispositivo(s) de ${u.name}`);
      else {
        const err = results.find(x => !x.ok);
        toast.error(`Enviado a ${sent}, falló en ${failed} dispositivo(s)${err?.status_code ? ` (HTTP ${err.status_code})` : ''}`);
      }
      const p = await api.get('/users/push-status');
      setPushStatus(p.data || {});
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al enviar prueba'); }
    finally { setTestingPush(null); }
  };

  const toggleStation = (sid) => setForm(f => ({ ...f, assigned_station_ids: f.assigned_station_ids.includes(sid) ? f.assigned_station_ids.filter(x=>x!==sid) : [...f.assigned_station_ids, sid] }));

  const roleLabel = (r) => r === 'admin' ? 'Admin' : r === 'territory_manager' ? 'Territory Mgr' : r === 'agent' ? 'Agente' : 'Capturador';
  const roleColor = (r) => r === 'admin' ? 'bg-[#1c1c1c] text-[#f8f6f1]' : r === 'territory_manager' ? 'bg-[#7c1d2e] text-[#f8f6f1]' : r === 'agent' ? 'bg-[#1e3a8a] text-[#f8f6f1]' : 'bg-[#f3f0e9] text-[#3a3a3a]';

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-serif text-[36px] leading-tight tracking-tight">Usuarios</h2>
          <p className="text-[13px] text-[#5a5a5a] mt-1">{users.length} miembros del equipo · {users.filter(u=>u.role==='territory_manager').length} territory managers</p>
        </div>
        <Button onClick={openCreate} className="h-10 rounded-full bg-[#1c1c1c] hover:bg-black text-[#f8f6f1] text-[12.5px]"><Plus size={14} className="mr-1.5"/> Invitar usuario</Button>
      </div>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(emptyForm); } }}>
        <DialogContent className="max-w-md bg-[#f8f6f1] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editing ? 'Editar usuario' : 'Invitar usuario'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-[12px] uppercase tracking-wider text-[#8a8579]">Nombre</Label>
              <Input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} className="mt-1 bg-white" data-testid="user-name-input"/>
            </div>
            {editing && (
              <div><Label className="text-[12px] uppercase tracking-wider text-[#8a8579]">Alias público</Label>
                <Input value={form.display_alias} onChange={e=>setForm({...form,display_alias:e.target.value})} placeholder='Ej. "Pricing 1" · vacío = usar nombre real' className="mt-1 bg-white" data-testid="user-alias-input"/>
                <p className="text-[10.5px] text-[#8a8579] mt-1">Si se define, todo lo que este usuario genere (pushes, alertas, notificaciones, previews) mostrará el alias en vez de su nombre. El nombre real solo queda en la bitácora de auditoría.</p>
              </div>
            )}
            <div><Label className="text-[12px] uppercase tracking-wider text-[#8a8579]">Correo</Label>
              <Input type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} className="mt-1 bg-white" data-testid="user-email-input"/>
              {editing && <p className="text-[10.5px] text-[#8a8579] mt-1">Al cambiar el correo, el usuario deberá iniciar sesión con el nuevo.</p>}
            </div>
            <div><Label className="text-[12px] uppercase tracking-wider text-[#8a8579]">Rol</Label>
              <Select value={form.role} onValueChange={v=>setForm({...form,role:v})}>
                <SelectTrigger className="mt-1 bg-white"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="capturer">Capturador (móvil)</SelectItem>
                  <SelectItem value="agent">Agente (recibe notificaciones)</SelectItem>
                  <SelectItem value="territory_manager">Territory Manager</SelectItem>
                  <SelectItem value="admin">Administrador</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.role === 'agent' && (
              <label className="flex items-center gap-2 cursor-pointer bg-white border border-[#e8e5de] rounded-lg px-3 py-2.5" data-testid="user-price-email-toggle">
                <input
                  type="checkbox"
                  checked={form.price_email_enabled !== false}
                  onChange={e => setForm({ ...form, price_email_enabled: e.target.checked })}
                  className="w-4 h-4"
                />
                <span className="text-[12.5px]">Enviar correos de cambios de precios a este agente</span>
              </label>
            )}

            {form.role === 'territory_manager' && (
              <div className="bg-white border border-[#e8e5de] rounded-lg p-3 space-y-3">
                <Label className="text-[11px] uppercase tracking-wider text-[#8a8579] flex items-center gap-1.5"><Building2 size={11}/> Configuración Territory Manager</Label>
                <div>
                  <Label className="text-[11px] text-[#5a5a5a]">Marca propia</Label>
                  <Select value={form.own_brand} onValueChange={v=>setForm({...form,own_brand:v})}>
                    <SelectTrigger className="mt-1 bg-white"><SelectValue/></SelectTrigger>
                    <SelectContent>{OWN_BRANDS.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[11px] text-[#5a5a5a]">Zona asignada</Label>
                  <Select value={form.zone_id || 'none'} onValueChange={v=>setForm({...form,zone_id:v==='none'?'':v})}>
                    <SelectTrigger className="mt-1 bg-white"><SelectValue placeholder="Selecciona zona…"/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Sin zona —</SelectItem>
                      {zones.filter(z => !z.own_brand || z.own_brand === form.own_brand).map(z =>
                        <SelectItem key={z.id} value={z.id}>{z.name} · {(z.station_ids?.length || 0)} estaciones</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-[10.5px] text-[#8a8579] mt-1">Recibirá alertas de las estaciones de esta zona (propias + competidores).</p>
                </div>
              </div>
            )}

            {(form.role === 'capturer' || form.role === 'agent') && (
              <div className="bg-white border border-[#e8e5de] rounded-lg p-3">
                <Label className="text-[11px] uppercase tracking-wider text-[#8a8579] flex items-center gap-1.5"><MapPin size={11}/> Estaciones asignadas ({form.assigned_station_ids.length})</Label>
                <p className="text-[10.5px] text-[#92400e] bg-[#fef3c7] border border-[#fde68a] rounded px-2 py-1 my-2">Debes asignar al menos una estación. Si dejas vacío, {form.role === 'agent' ? 'el agente no recibirá notificaciones' : 'el capturador no verá ninguna estación'}.</p>
                <div className="flex items-center gap-2 mb-2">
                  <div className="relative flex-1">
                    <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8a8579]"/>
                    <Input
                      value={stationQ}
                      onChange={e => setStationQ(e.target.value)}
                      placeholder="Buscar por nombre, marca, BU o dirección…"
                      className="pl-8 bg-[#f8f6f1] text-[12px] h-8"
                      data-testid="user-station-search"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setOnlyOwn(v => !v)}
                    className={`text-[11px] px-2.5 h-8 rounded-full border shrink-0 ${onlyOwn ? 'bg-[#1c1c1c] text-[#f8f6f1] border-[#1c1c1c]' : 'bg-white text-[#5a5a5a] border-[#d4cfc4]'}`}
                    data-testid="user-station-onlyown-toggle"
                    title="Mostrar solo estaciones propias"
                  >
                    Solo propias
                  </button>
                </div>
                <div className="space-y-1 max-h-48 overflow-y-auto" data-testid="user-station-list">
                  {stationOptions.map(s => (
                    <label key={s.id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-[#f3f0e9] cursor-pointer">
                      <input type="checkbox" checked={form.assigned_station_ids.includes(s.id)} onChange={() => toggleStation(s.id)} className="w-4 h-4"/>
                      <span className="text-[12px] flex-1 truncate">{s.name}</span>
                      <span className="text-[10px] text-[#8a8579]">{s.brand}{s.is_own ? ' · propia' : ''}</span>
                    </label>
                  ))}
                  {stationOptions.length === 0 && <p className="text-[11px] text-[#a8a397] py-2 px-2">Sin resultados.</p>}
                </div>
              </div>
            )}

            {!editing && (
              <div><Label className="text-[12px] uppercase tracking-wider text-[#8a8579]">Contraseña (opcional)</Label>
                <Input type="text" value={form.password} onChange={e=>setForm({...form,password:e.target.value})} placeholder="Vacío → generada automáticamente" className="mt-1 bg-white"/>
              </div>
            )}
            {editing && (
              <div><Label className="text-[12px] uppercase tracking-wider text-[#8a8579]">Nueva contraseña (opcional)</Label>
                <Input type="text" value={form.password} onChange={e=>setForm({...form,password:e.target.value})} placeholder="Dejar vacío para no cambiar · mínimo 4 caracteres" className="mt-1 bg-white" data-testid="input-edit-password"/>
                <p className="text-[10.5px] text-[#8a8579] mt-1">Solo se cambia si ingresas un valor.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Cancelar</Button>
            <Button onClick={submit} className="bg-[#1c1c1c] text-[#f8f6f1] hover:bg-black">{editing ? 'Guardar' : 'Crear usuario'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!createdUser} onOpenChange={() => setCreatedUser(null)}>
        <DialogContent className="max-w-md bg-[#f8f6f1]">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Usuario creado</DialogTitle></DialogHeader>
          <p className="text-[13px] text-[#5a5a5a]">Comparte estas credenciales con <span className="font-medium">{createdUser?.name}</span>:</p>
          <div className="bg-white border border-[#e8e5de] rounded-lg p-4 space-y-2 font-mono text-[12.5px]">
            <div className="flex justify-between"><span className="text-[#8a8579]">correo</span><span>{createdUser?.email}</span></div>
            <div className="flex justify-between items-center"><span className="text-[#8a8579]">contraseña inicial</span>
              <span className="flex items-center gap-2">{createdUser?.initial_password}
                <button onClick={() => { navigator.clipboard.writeText(createdUser?.initial_password); toast.success('Copiada'); }} className="text-[#1c1c1c]"><Copy size={12}/></button>
              </span>
            </div>
          </div>
          <DialogFooter><Button onClick={() => setCreatedUser(null)} className="bg-[#1c1c1c] text-[#f8f6f1]">Listo</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a8579]"/>
        <Input value={q} onChange={e=>setQ(e.target.value)} placeholder="Buscar usuario…" className="pl-9 bg-white border-[#e8e5de] rounded-full text-[13px] h-10"/>
      </div>

      {pendingUsers.length > 0 && (
        <div className="bg-[#fef3c7] border border-[#fde68a] rounded-xl p-5" data-testid="pending-users-section">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-serif text-[20px] tracking-tight text-[#92400e]">Solicitudes de acceso</h3>
              <p className="text-[12px] text-[#92400e]/80">{pendingUsers.length} cuenta(s) esperando aprobación</p>
            </div>
          </div>
          <div className="space-y-2">
            {pendingUsers.map(u => (
              <div key={u.id} className="bg-white border border-[#fde68a] rounded-lg p-3 flex items-center justify-between gap-3" data-testid={`pending-user-${u.email}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-[#1c1c1c] text-[#f8f6f1] flex items-center justify-center text-[12px] font-medium shrink-0">{u.name.split(' ').map(n => n[0]).join('').slice(0,2)}</div>
                  <div className="min-w-0">
                    <p className="text-[13px] font-medium truncate">{u.name}{u.display_alias ? <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-[#e0e7ff] text-[#3730a3] font-normal">alias: {u.display_alias}</span> : null}</p>
                    <p className="text-[11.5px] text-[#5a5a5a] truncate">{u.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button onClick={() => rejectUser(u)} variant="outline" className="h-8 px-3 text-[12px] border-[#d4cfc4] text-[#7c1d2e] hover:bg-[#fee2e2]" data-testid={`reject-user-${u.email}`}>
                    <X size={13} className="mr-1"/> Rechazar
                  </Button>
                  <Button onClick={() => approveUser(u)} className="h-8 px-3 text-[12px] bg-[#15803d] hover:bg-[#166534] text-white" data-testid={`approve-user-${u.email}`}>
                    <Check size={13} className="mr-1"/> Aprobar
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-[#e8e5de] rounded-xl overflow-hidden">
        {loading ? <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-[#8a8579]"/></div> : (
          <table className="w-full text-[13px]">
            <thead className="bg-[#f8f6f1]">
              <tr className="text-left text-[11px] tracking-wider uppercase text-[#8a8579] border-b border-[#e8e5de]">
                <th className="py-3 px-4 font-normal">Nombre</th>
                <th className="py-3 px-2 font-normal">Correo</th>
                <th className="py-3 px-2 font-normal">Rol</th>
                <th className="py-3 px-2 font-normal">Cobertura</th>
                <th className="py-3 px-2 font-normal">Última actividad</th>
                <th className="py-3 px-2 font-normal">Push</th>
                <th className="py-3 px-2 font-normal text-right">Estado</th>
                <th className="py-3 px-4 font-normal text-right w-32">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#e8e5de]">
              {filtered.map(u => (
                <tr key={u.id} className="hover:bg-[#fafaf6] cursor-pointer" onClick={() => openEdit(u)}>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#1c1c1c] text-[#f8f6f1] flex items-center justify-center text-[11px] font-medium">{u.name.split(' ').map(n => n[0]).join('').slice(0,2)}</div>
                      <p className="font-medium">{u.name}{u.display_alias ? <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-[#e0e7ff] text-[#3730a3] font-normal" data-testid={`user-alias-chip-${u.email}`}>alias: {u.display_alias}</span> : null}</p>
                    </div>
                  </td>
                  <td className="py-3 px-2 text-[#3a3a3a]"><span className="flex items-center gap-1.5"><Mail size={11}/> {u.email}</span></td>
                  <td className="py-3 px-2"><span className={`text-[10px] px-2 py-0.5 rounded-full ${roleColor(u.role)}`}>{roleLabel(u.role)}</span></td>
                  <td className="py-3 px-2 text-[#3a3a3a] text-[12px]">
                    {u.role === 'territory_manager' ? (
                      <span>{u.own_brand || '—'} · {zones.find(z => z.id === u.zone_id)?.name || <span className="text-[#8a8579]">sin zona</span>}</span>
                    ) : u.role === 'capturer' && (u.assigned_station_ids || []).length > 0 ? (
                      <span>{(u.assigned_station_ids || []).length} estaciones</span>
                    ) : (
                      <span className="text-[#8a8579]">—</span>
                    )}
                  </td>
                  <td className="py-3 px-2 text-[12px] text-[#5a5a5a]">{u.last_active ? new Date(u.last_active).toLocaleString('es-GT', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</td>
                  <td className="py-3 px-2" onClick={e => e.stopPropagation()}>
                    {(() => {
                      const ps = pushStatus[u.id];
                      const subs = ps?.subscriptions || 0;
                      const last = ps?.last_delivery;
                      return (
                        <span className="flex items-center gap-1.5 text-[11.5px]" data-testid={`push-status-${u.email}`} title={last ? `Último envío: ${last.ok ? 'entregado al servicio push' : `error${last.status_code ? ' HTTP ' + last.status_code : ''}`} · ${new Date(last.at).toLocaleString('es-GT')}` : 'Sin envíos registrados'}>
                          {subs === 0 ? (
                            <span className="text-[#a8a397]">Sin dispositivos</span>
                          ) : (
                            <>
                              <span className={`w-2 h-2 rounded-full shrink-0 ${!last ? 'bg-[#d4cfc4]' : last.ok ? 'bg-[#15803d]' : 'bg-[#b91c1c]'}`}/>
                              <span className="text-[#3a3a3a]">{subs} disp.</span>
                            </>
                          )}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="py-3 px-2 text-right" onClick={e => e.stopPropagation()}>
                    {u.status === 'pending' ? (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#fef3c7] text-[#92400e] border border-[#fde68a]">Pendiente</span>
                    ) : (
                      <Switch checked={u.status === 'active'} onCheckedChange={() => toggleStatus(u)}/>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => sendTestPush(u)}
                        disabled={testingPush === u.id}
                        title="Enviar push de prueba"
                        className="w-7 h-7 rounded-md hover:bg-[#f3f0e9] flex items-center justify-center text-[#1e3a8a] disabled:opacity-50"
                        data-testid={`btn-test-push-${u.email}`}
                      >
                        {testingPush === u.id ? <Loader2 size={13} className="animate-spin"/> : <BellRing size={13}/>}
                      </button>
                      <button
                        onClick={() => openEdit(u)}
                        title="Editar / cambiar contraseña"
                        className="w-7 h-7 rounded-md hover:bg-[#f3f0e9] flex items-center justify-center text-[#1c1c1c]"
                        data-testid={`btn-edit-user-${u.email}`}
                      >
                        <Key size={13}/>
                      </button>
                      <button
                        onClick={() => deleteUser(u)}
                        title="Eliminar usuario"
                        className="w-7 h-7 rounded-md hover:bg-[#fee2e2] flex items-center justify-center text-[#7c1d2e]"
                        data-testid={`btn-delete-user-${u.email}`}
                      >
                        <Trash2 size={13}/>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdminUsers;
